/**
 * @description 网易七鱼客服 H5 接入封装（Tenda PC）
 */

import { STORAGE_NAMES, storageKey } from '~/utils/storageKey'

const QIYU_CLIENT_ORIGIN = 'https://szsjxtdkjyxgs3.qiyukf.com'

const QIYU_BASE_CONFIG = {
  appKey: '31e1cea24856315116d0960807a79ef0',
  robotId: '6614766',
  language: 'zh-cn',
  wp: '1',
  robotShuntSwitch: '1',
}

/** @type {{ groupid: string, templateId: string, qtype: string }} */
const QIYU_CONFIG_SALES = {
  // 销售咨询
  groupid: '700879451',
  templateId: '8142224',
  qtype: '5507149',
}

/** @type {{ groupid: string, templateId: string, qtype: string }} */
const QIYU_CONFIG_AFTER_SALES = {
  // 售后咨询
  groupid: '700880141',
  templateId: '8145186',
  qtype: '5513141',
}

/** @type {{ groupid: string, templateId: string, qtype: string }} */
const QIYU_CONFIG_SCHEME = {
  // 方案咨询
  groupid: '700880142',
  templateId: '8147148',
  qtype: '5518137',
}

export const SERVICE_TYPE = {
  SALES: 'sales',
  AFTER_SALES: 'afterSales',
  SCHEME: 'scheme',
}

/**
 * @description 按场景获取七鱼配置
 * @param {string} [serviceType='sales']
 * @returns {typeof QIYU_BASE_CONFIG & { groupid: string, templateId: string, qtype: string }}
 */
const getServiceConfig = (serviceType = SERVICE_TYPE.SALES) => {
  const sceneConfig =
    serviceType === SERVICE_TYPE.AFTER_SALES
      ? QIYU_CONFIG_AFTER_SALES
      : serviceType === SERVICE_TYPE.SCHEME
        ? QIYU_CONFIG_SCHEME
        : QIYU_CONFIG_SALES

  return {
    ...QIYU_BASE_CONFIG,
    ...sceneConfig,
  }
}

/**
 * @description 获取或生成访客唯一标识（未登录场景，与 ip-com 一致）
 * @returns {string}
 */
const getGuestId = () => {
  if (!import.meta.client) return ''
  const guestIdCookie = useCookie(storageKey(STORAGE_NAMES.QIYU_GUEST_ID), {
    maxAge: 60 * 60 * 24 * 365,
  })
  if (!guestIdCookie.value) {
    guestIdCookie.value = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  }
  return guestIdCookie.value
}

/**
 * @description 解析七鱼访客 uid：登录用户优先 userId/id，未登录回退 guestId
 * @reason 对齐 ip-com 的 `config.uid = userId || guestId`，保证同一用户跨会话可识别
 * @param {Object|null|undefined} [userInfo]
 * @returns {string}
 */
const resolveVisitorUid = (userInfo) => {
  const userId = userInfo?.userId ?? userInfo?.id
  if (userId !== undefined && userId !== null && String(userId).trim() !== '') {
    return String(userId)
  }
  return getGuestId() || String(Date.now())
}

/**
 * @description 构建七鱼访客端地址
 * @param {ReturnType<typeof getServiceConfig>} serviceConfig
 * @param {string} uid - 七鱼访客唯一标识（对应 URL 参数 u）
 * @returns {string}
 */
const buildQiyuClientUrl = (serviceConfig, uid) => {
  const params = new URLSearchParams({
    k: serviceConfig.appKey,
    wp: serviceConfig.wp,
    robotShuntSwitch: serviceConfig.robotShuntSwitch,
    robotId: serviceConfig.robotId,
    gid: serviceConfig.groupid,
    language: serviceConfig.language,
    templateId: serviceConfig.templateId,
    qtype: serviceConfig.qtype,
    u: uid,
  })

  return `${QIYU_CLIENT_ORIGIN}/client?${params.toString()}`
}

/**
 * @description 打开客服窗口
 * @param {string} serviceType
 * @param {Object|null} [userInfo] - 本项目 userStore.userInfo（含 userId/id、nick_name、account_name 等）
 * @param {Array} [_kefuInfo] - 预留扩展字段，当前直链打开无需 SDK 透传
 */
export const openService = (serviceType = SERVICE_TYPE.SALES, userInfo = {}, _kefuInfo = []) => {
  if (!import.meta.client) return

  const serviceConfig = getServiceConfig(serviceType)
  const uid = resolveVisitorUid(userInfo)

  try {
    window.open(buildQiyuClientUrl(serviceConfig, uid), '_blank', 'noopener,noreferrer')
  } catch (err) {
    console.error('打开客服失败', err)
  }
}

export default {
  openService,
  SERVICE_TYPE,
}
